import { NextPage } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect } from 'react';
import ConnexionFlowLayout from '@components/ConnexionFlowLayout';
import EnrollSmsMfa, { MFA_ENROLLED_QUERY_KEY } from '@components/EnrollSmsMfa';
import { sendOnboardingEmail } from '@functions';
import { APP_NAME } from '@utils/consts/livv';
import { useAuth } from '@utils/context/auth';
import useUser from '@utils/context/user';
import useAlertSnackBar from '@utils/hooks/useAlertSnackBar';
import useSession from '@utils/hooks/useUserSession';

const withSmsMfaEnrollment =
    <P extends object>(Component: NextPage<P>): NextPage<P> =>
    (props) => {
        const [successSnackBar, setSuccessMessage] = useAlertSnackBar({
            anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
            severity: 'success',
        });
        const { t } = useTranslation('common');
        const { user, isLoading: isAuthLoading } = useAuth();
        const { loading: isUserLoading, privileges } = useUser();
        const router = useRouter();
        const { isLoading: isUserSessionLoading } = useSession();

        useEffect(() => {
            if (user && user.emailVerified && router.query[MFA_ENROLLED_QUERY_KEY]) {
                setSuccessMessage(t('smsMfa:enrollment.success', { appName: APP_NAME }));
                window.history.replaceState(
                    null,
                    '',
                    router.asPath.replace(
                        new RegExp(`(\\?|\\&)${MFA_ENROLLED_QUERY_KEY}=true`),
                        '',
                    ),
                );

                sendOnboardingEmail({ uid: user.uid });
            }
            // Display message only once
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [user]);

        // Wait for all loading states to resolve before checking conditions
        if (isAuthLoading || isUserLoading || isUserSessionLoading) {
            return null;
        }

        // Show SMS 2FA enrollment if user is logged in, email is verified, but SMS 2FA is not enrolled
        if (user && privileges && !privileges.isMfaEnrolled && privileges.isEmailVerified) {
            return (
                <ConnexionFlowLayout>
                    <EnrollSmsMfa />
                </ConnexionFlowLayout>
            );
        }

        return (
            <>
                <Component {...props} />
                {successSnackBar}
            </>
        );
    };

export default withSmsMfaEnrollment;
